import { batch, createEffect, createMemo, onCleanup, Show, untrack } from "solid-js"
import { createStore, reconcile } from "solid-js/store"
import { useGlobal } from "@/context/global"
import { useLanguage } from "@/context/language"
import { useServer } from "@/context/server"
import { listSessions } from "@/cowork/api"
import { ensureOpenCodeServer } from "@/cowork/open-chat"
import { belongsToWorkflow, resultTabs, savedWorkflowResult, tabsForStage, workflowRootIDs, workflowSessionActivity, workflowStatusEvent,
  type ResultTab, type SessionStatus, type WorkflowScope } from "@/cowork/workflow-activity"

export function WorkflowActivity(props: WorkflowScope & { onResultsSaved: (tabs: ResultTab[]) => void; onActivity?: (tabs: ResultTab[]) => void }) {
  const language = useLanguage()
  const server = useServer()
  const global = useGlobal()
  let runtime: ReturnType<typeof global.ensureServerCtx> | undefined
  let resultsSettled = () => {}
  const [state, setState] = createStore({ loaded: false, error: false, roots: [] as string[], statuses: {} as Record<string, SessionStatus> })
  const scopeKey = createMemo(() => JSON.stringify([props.companyID, props.projectID, props.periodID ?? null, props.remoteSessionID ?? null]))

  createEffect(() => {
    const [companyID, projectID, periodID, remoteSessionID] = JSON.parse(scopeKey()) as [string, string, string | null, string | null]
    const scope: WorkflowScope = { companyID, projectID, periodID, remoteSessionID: remoteSessionID ?? undefined }
    runtime = undefined
    setState(reconcile({ loaded: false, error: false, roots: scope.remoteSessionID ? [scope.remoteSessionID] : [], statuses: {} }))
    let disposed = false
    let fetching = false
    let revision = 0
    let unsubscribe = () => {}
    let poll: ReturnType<typeof setInterval> | undefined
    let flushTimer: ReturnType<typeof setTimeout> | undefined
    const updates = new Map<string, { status: SessionStatus; revision: number }>()
    const saved = new Set<string>()
    const pending = new Set<ResultTab>()
    const flush = () => {
      flushTimer = undefined
      if (disposed || document.hidden || !pending.size) return
      const tabs = [...pending]
      pending.clear()
      props.onResultsSaved(tabs)
    }
    const queue = (tabs: readonly ResultTab[]) => {
      tabs.forEach((tab) => pending.add(tab))
      if (!flushTimer && !disposed) flushTimer = setTimeout(flush, 400)
    }
    resultsSettled = () => queue(resultTabs)
    const refresh = async () => {
      const connection = runtime
      if (!connection || disposed || fetching || document.hidden) return
      fetching = true
      const started = revision
      try {
        const [sessions, snapshot] = await Promise.all([
          scope.remoteSessionID ? [] : listSessions(scope.projectID, scope.periodID ?? undefined, scope.companyID),
          connection.sdk.protocol.then(async (protocol): Promise<Record<string, SessionStatus>> => {
            if (protocol === "v1") {
              const response = await connection.sdk.client.session.status()
              return Object.fromEntries(Object.entries(response.data ?? {}).map(([id, status]) => [id, status.type]))
            }
            const active = await connection.sdk.api.session.active()
            return Object.fromEntries(Object.keys(active).map((id) => [id, "busy"]))
          }),
        ])
        if (disposed) return
        const roots = scope.remoteSessionID ? [scope.remoteSessionID] : workflowRootIDs(sessions, scope)
        for (const [id, event] of updates) {
          if (event.revision > started) snapshot[id] = event.status
          else updates.delete(id)
        }
        const statuses = Object.fromEntries(Object.entries(snapshot).filter(([id]) => belongsToWorkflow(id, roots, connection.sync.session.data.info)))
        batch(() => { setState("statuses", reconcile(statuses)); setState({ roots, loaded: true, error: false }) })
      } catch {
        if (!disposed) setState("error", true)
      } finally { fetching = false }
    }
    const resume = () => { void refresh(); if (pending.size && !flushTimer) flushTimer = setTimeout(flush, 0) }
    window.addEventListener("focus", resume)
    document.addEventListener("visibilitychange", resume)
    void ensureOpenCodeServer(server, scope.companyID).then((connection) => {
      if (disposed) return
      runtime = global.ensureServerCtx(connection)
      const current = runtime
      unsubscribe = current.sdk.event.listen(({ details }) => {
        if (disposed) return
        const belongs = (id: string) => belongsToWorkflow(id, state.roots, current.sync.session.data.info)
        const update = workflowStatusEvent(details)
        if (update && (belongs(update.sessionID) || !state.loaded)) {
          updates.set(update.sessionID, { status: update.status, revision: ++revision })
          if (updates.size > 256) updates.delete(updates.keys().next().value!)
          if (belongs(update.sessionID)) {
            const previous = state.statuses[update.sessionID]
            setState("statuses", update.sessionID, update.status)
            if (update.status === "idle" && previous && previous !== "idle") queue(resultTabs)
          }
        }
        const result = savedWorkflowResult(details, current.sync.session.data.part)
        if (result && belongs(result.sessionID) && !saved.has(result.key)) {
          saved.add(result.key)
          if (saved.size > 256) saved.delete(saved.values().next().value!)
          queue(result.tabs)
        }
        if (details.type === "server.connected") { void refresh(); queue(resultTabs) }
      })
      void refresh()
      poll = setInterval(() => void refresh(), 10000)
    }).catch(() => { if (!disposed) setState("error", true) })
    onCleanup(() => {
      disposed = true
      unsubscribe()
      clearInterval(poll)
      clearTimeout(flushTimer)
      window.removeEventListener("focus", resume)
      document.removeEventListener("visibilitychange", resume)
    })
  })

  const relevant = createMemo(() => !state.loaded || !runtime ? [] : [...new Set([...state.roots, ...Object.keys(state.statuses)])]
    .filter((id) => belongsToWorkflow(id, state.roots, runtime!.sync.session.data.info)))
  const waiting = (id: string) => !!runtime?.sync.session.data.question[id]?.length || !!runtime?.sync.session.data.permission[id]?.length
  const active = createMemo(() => relevant().filter((id) => state.statuses[id] === "busy" && !waiting(id)))
  const hydrated = new Set<string>()
  createEffect(() => {
    const ids = active()
    for (const id of hydrated) if (!ids.includes(id)) hydrated.delete(id)
    for (const id of ids) {
      if (!runtime || hydrated.has(id)) continue
      hydrated.add(id)
      void runtime.sync.session.sync(id, { messageLimit: 5 }).catch(() => hydrated.delete(id))
    }
  })
  const phase = createMemo(() => state.error ? "unavailable" : !state.loaded ? "loading" : active().length ? "processing" :
    relevant().some((id) => waiting(id)) ? "waiting" : relevant().some((id) => state.statuses[id] === "retry") ? "retrying" : "idle")
  const label = createMemo(() => {
    if (phase() !== "processing" || !runtime) return phase()
    const data = runtime.sync.session.data
    return active().flatMap((id) => { const activity = workflowSessionActivity(data.message[id] ?? [], data.part); return activity ? [activity] : [] })
      .sort((a, b) => b.priority - a.priority || b.time - a.time)[0]?.stage ?? "processing"
  })
  let previous = "loading"
  createEffect(() => {
    const next = phase()
    if (next === "idle" && ["processing", "retrying", "waiting"].includes(previous)) untrack(resultsSettled)
    previous = next
    const tabs = next === "processing" ? tabsForStage(label()) : []
    untrack(() => props.onActivity?.(tabs))
  })
  onCleanup(() => props.onActivity?.([]))
  return <div class="flex min-w-0 items-center gap-2 text-12-regular text-v2-text-text-muted" role="status" aria-live="polite" aria-atomic="true" data-workflow-activity={phase()}>
    <span aria-hidden="true" class="size-2 shrink-0 rounded-full bg-current" classList={{ "animate-pulse": phase() === "processing" || phase() === "retrying" }} />
    <span>{language.t(`cowork.workflow.activity.${label()}`)}</span>
  </div>
}

export function WorkflowTabIndicator(props: { complete?: boolean; working?: boolean }) {
  const language = useLanguage()
  return <Show when={props.complete || props.working}><span role="img" class="ml-2 inline-block size-2 rounded-full"
    classList={{ "animate-pulse bg-current": !!props.working, "bg-v2-state-fg-success": !props.working && !!props.complete }}
    aria-label={language.t(props.working ? "cowork.workflow.activity.active" : "cowork.workflow.activity.saved")}
    title={language.t(props.working ? "cowork.workflow.activity.active" : "cowork.workflow.activity.saved")} /></Show>
}
